import { uuidV4 } from '@readcloud/common';
import { Annotation } from '@readcloud/data';
import {
  annotationsActions,
  annotationsSelectors,
  applicationActions,
  applicationSelectors,
  authSelectors,
  bookmarksActions,
  cloudSelectors,
  jobsActions,
  readerActions,
  readerSelectors,
  reportActions,
  useAppDispatch,
  usersSelectors,
} from '@readcloud/state';
import { ObjectId } from 'bson';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { useComponentSize } from '../../hooks/useComponentSize';
import { makePageLabelsByLocation } from '../../constants';
import { epubThemes } from './constants';
import { EpubViewer } from './EpubViewer';
import { useLocation } from 'react-router-dom';

interface IConnectedEPUBViewerProps {
  container: React.RefObject<HTMLDivElement>;
}

export const ConnectedEPUBViewer: React.FC<IConnectedEPUBViewerProps> = (
  props
) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const appData = useSelector(applicationSelectors.getApplcationState);
  const documentControl = useSelector(readerSelectors.getDocumentControl);
  const user = useSelector(authSelectors.getUser);
  const knownUsers = useSelector(usersSelectors.getKnownUsers);
  const knownClouds = useSelector(cloudSelectors.getKnownClouds);
  const selectedAnnotation = useSelector(
    annotationsSelectors.getSelectedAnnotation
  );

  const { setFindController, setOutline } = useContext(
    DocumentWithExtrasContext
  );

  const annotations = useSelector(annotationsSelectors.getAnnotationsByBook);

  const dispatch = useAppDispatch();

  const containerSize = useComponentSize(props.container);
  const routerLocation = useLocation();
  const pageLocation = new URLSearchParams(routerLocation.search).get('p');

  const handleEditAnnotation = (annotation: Annotation) => {
    if (!annotation) {
      return;
    }
    dispatch(annotationsActions.setSelectedAnnotation(annotation.id));

    dispatch(
      applicationActions.updateApplication({
        annotationUpdateModalOpen: true,
      })
    );
  };

  const handleEpubKeyUp = (e: KeyboardEvent) => {
    if (e.keyCode === 39) {
      dispatch(readerActions.updateDocumentControl({ type: 'next' }));
    } else if (e.keyCode === 37) {
      dispatch(readerActions.updateDocumentControl({ type: 'prev' }));
    }
  };

  return readerData.bookId && readerData.cloudId ? (
    <EpubViewer
      height={containerSize.height}
      width={containerSize.width}
      disableTTS={user?.permissions?.indexOf('TTS') !== -1}
      onSectionChange={(section) => {
        if (section) {
          section.focus();
        }
      }}
      onFindController={(findController) => setFindController(findController)}
      onToggleToolbars={(show) => {
        if (typeof show === 'boolean') {
          dispatch(
            applicationActions.updateApplication({ toolbarsOpen: show })
          );
        } else {
          dispatch(applicationActions.toggleToolbars());
        }
      }}
      onReadStat={(readStatEvent) =>
        dispatch(reportActions.asyncActions.createReadStat(readStatEvent))
      }
      onSelectAnnotation={(id) =>
        dispatch(annotationsActions.setSelectedAnnotation(id))
      }
      onDeselectAnnotation={() => {
        dispatch(annotationsActions.setSelectedAnnotation(''));
      }}
      cloudId={readerData.cloudId}
      knownClouds={knownClouds}
      knownUsers={knownUsers}
      onBeforeDisplay={() => {}}
      initialLocation={pageLocation || readerData?.viewState?.location}
      themes={epubThemes}
      theme={readerData?.viewState?.epubMode}
      fontFamily={readerData?.viewState?.epubFont}
      fontSize={`${readerData?.viewState?.epubFontSize}rem`}
      onEditAnnotation={handleEditAnnotation}
      onDeleteAnnotation={(id) =>
        dispatch(annotationsActions.asyncActions.deleteAnnotation(id))
      }
      onAddBookmark={({ text, location }) => {
        if (!(user && readerData.bookId && readerData.cloudId)) {
          return;
        }
        dispatch(
          bookmarksActions.asyncActions.addBookmark({
            authorId: user.id,
            bookId: readerData.bookId,
            cloudIds: [readerData.cloudId],
            created: new Date().toISOString(),
            updated: new Date().toISOString(),
            clientCreated: new Date().toISOString(),
            clientUpdated: new Date().toISOString(),
            deleted: false,
            id: new ObjectId().valueOf().toString(),
            text: text || '',
            location,
            version: '2',
          })
        ).then(() => {
          dispatch(
            jobsActions.addJob({
              id: uuidV4(),
              description: `Bookmark added on page ${location}!`,
            })
          );
        });
      }}
      onTocChange={(outline) => setOutline(outline)}
      onAddAnnotation={({
        pageNumber,
        color,
        selectedText,
        text,
        shared,
        cfiRange,
      }) => {
        if (user && readerData.bookId && readerData.cloudId) {
          dispatch(
            annotationsActions.asyncActions.addAnnotation({
              version: '2',
              deleted: false,
              author: user.id,
              book: readerData.bookId,
              bookType: 'EPUB',
              color,
              created: new Date().toISOString(),
              updated: new Date().toISOString(),
              clientCreated: new Date().toISOString(),
              clientUpdated: new Date().toISOString(),
              id: new ObjectId().valueOf().toString(),
              location: {
                range: cfiRange,
                pageNumber,
              },
              text,
              selectedText,
              tags: [],
              visibleToUsers: [],
              visibleInClouds: shared ? [readerData.cloudId] : [],
              type: 'Text',
            })
          );
        }
      }}
      selectedAnnotationId={selectedAnnotation?.id}
      annotations={annotations}
      onKeyUp={handleEpubKeyUp}
      onPageLabelsChange={(pageLabels) =>
        dispatch(
          readerActions.updateReaderData({
            pageLabels,
            pageLabelsByLocation: makePageLabelsByLocation(pageLabels),
            loadingPageLabels: false,
            numPages: pageLabels.length,
          })
        )
      }
      control={documentControl}
      setDocumentControl={(control) =>
        dispatch(readerActions.updateDocumentControl(control))
      }
      getImmersiveToken={() =>
        dispatch(applicationActions.asyncActions.getImmersiveToken())
      }
      isOnline={appData.online}
      checkOnline={() => true} // TODO: make this actually do something.
      toggleIOSInterceptor={(status) =>
        dispatch(
          applicationActions.asyncActions.updateNativeInterceptor(status)
        )
      }
      epubPages={readerData?.bookOptions?.epubPages}
      onUpdateEpubPages={(locations: string) =>
        dispatch(
          readerActions.asyncActions.updateEpubPages({
            bookId: readerData.bookId || '',
            epubPages: locations,
          })
        )
      }
    />
  ) : null;
};
